import React from 'react';
import { formatRelative } from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import GenericNotification from 'components/notifications/GenericNotification';
import SessionCreatedNotification from 'components/notifications/SessionCreatedNotification';
import SessionDeletedNotification from 'components/notifications/SessionDeletedNotification';
import ExerciseRequestApprovedNotification from 'components/notifications/ExerciseRequestApprovedNotification';
import ExerciseRequestCreatedNotification from 'components/notifications/ExerciseRequestCreatedNotification';
import ExerciseRequestRejectedNotification from 'components/notifications/ExerciseRequestRejectedNotification';
import NewsCreatedNotification from 'components/notifications/NewsCreatedNotification';
import GroupSessionCreatedNotification from 'components/notifications/GroupSessionCreatedNotification';
import PlanAcquisitionRequestedNotification from 'components/notifications/PlanAcquisitionRequestedNotification';
import PlanRequestApprovedNotification from 'components/notifications/PlanRequestApprovedNotification';
import PlanRequestRejectedNotification from 'components/notifications/PlanRequestRejectedNotification';
import UserRegisteredNotification from 'components/notifications/UserRegisteredNotification';
import { roles } from 'common/constants/options';
import { createSnack } from 'common/utils/general';
import i18n from 'i18n';

export const arePushNotificationsSupported = 'Notification' in window;

export const failNotificationPermissionSnack = createSnack({
  key: 'push-notification-permission-fail',
  variant: 'error',
  message: i18n.t('notification.pushNotificationFail', { ns: 'errors' })
});

const formatCreationDate = (date, locale) => {
  const targetDate = date ? new Date(`${date}Z`) : new Date();

  // Una fecha inválida hace que formatRelative LANCE; devolvemos '' para no romper el
  // render de la notificación (y con ello toda la lista de la campana).
  if (Number.isNaN(targetDate.getTime())) return '';

  return formatRelative(targetDate, new Date(), {
    locale: locale === 'es' ? es : enUS,
    weekStartsOn: 1
  });
};

export const getNotificationType = (notification) => {
  const notificationToRender = {
    'session.created': () => <SessionCreatedNotification notification={notification} />,
    'session.deleted.client': () => (
      <SessionDeletedNotification deletedBy={roles.client} notification={notification} />
    ),
    'session.deleted.trainer': () => (
      <SessionDeletedNotification deletedBy={roles.trainer} notification={notification} />
    ),
    'exercise.request.approved': () => <ExerciseRequestApprovedNotification notification={notification} />,
    'exercise.request.created': () => <ExerciseRequestCreatedNotification notification={notification} />,
    'exercise.request.rejected': () => <ExerciseRequestRejectedNotification notification={notification} />,
    'news.created': () => <NewsCreatedNotification notification={notification} />,
    'news.updated': () => <NewsCreatedNotification notification={notification} />,
    'group.session.created': () => <GroupSessionCreatedNotification notification={notification} />,
    'plan.acquisition_requested': () => <PlanAcquisitionRequestedNotification notification={notification} />,
    'plan.request.approved': () => <PlanRequestApprovedNotification notification={notification} />,
    'plan.request.rejected': () => <PlanRequestRejectedNotification notification={notification} />,
    'user.registered': () => <UserRegisteredNotification notification={notification} />,
    'reward_approved':     () => <GenericNotification notification={notification} icon={notification.data?.icon || 'gift'} color={notification.data?.color} />,
    'rank_up':             () => <GenericNotification notification={notification} icon="trophy"                            color={notification.data?.color} />,
    // El backend emite 'top_3_monthly' (Top3MonthlyNotification); 'top3_monthly' se conserva
    // por las notificaciones viejas ya guardadas con esa clave.
    'top3_monthly':        () => <GenericNotification notification={notification} icon="trophy"                            color={notification.data?.color} />,
    'top_3_monthly':       () => <GenericNotification notification={notification} icon="trophy"                            color={notification.data?.color} />,
    'weekly_summary':      () => <GenericNotification notification={notification} icon="star"                              color={notification.data?.color} />,
    'admin_top3_approval': () => <GenericNotification notification={notification} icon="check"                             color={notification.data?.color} />,
    // PQRS / progreso / Play: renderer genérico con icono (antes solo salían si traían title).
    'pqrs.created':        () => <GenericNotification notification={notification} icon="check"  color={notification.data?.color} />,
    'pqrs.replied':        () => <GenericNotification notification={notification} icon="check"  color={notification.data?.color} />,
    'progress.improved':   () => <GenericNotification notification={notification} icon="trophy" color={notification.data?.color} />,
    'progress.observation':() => <GenericNotification notification={notification} icon="star"   color={notification.data?.color} />,
    // Un cliente registró progreso (entrenador con acceso) o anexó sus datos (equipo).
    'progress.submitted':  () => <GenericNotification notification={notification} icon={notification.data?.icon || 'camera'} color={notification.data?.color} />,
    'progress.data_added': () => <GenericNotification notification={notification} icon="chart-line" color={notification.data?.color} />,
    'play.routine_adopted':  () => <GenericNotification notification={notification} icon="star"   color={notification.data?.color} />,
    'play.result_reviewed':  () => <GenericNotification notification={notification} icon="check"  color={notification.data?.color} />,
    'play.routine_moderated':() => <GenericNotification notification={notification} icon="check"  color={notification.data?.color} />,
    // Bienestar corporativo: alerta semanal de caída del índice (solo admin/RRHH).
    'wellbeing.alert':     () => <GenericNotification notification={notification} icon="warning" color={notification.data?.color} />,
    // Una empresa cliente pide habilitar otro plan para sus colaboradores.
    'organization.plan_requested': () => <GenericNotification notification={notification} icon="award" color={notification.data?.color} />
  };

  const renderer = notificationToRender[notification.type];
  if (renderer) return renderer();

  if (notification.data?.title) {
    return (
      <GenericNotification
        notification={notification}
        icon={notification.data.icon || 'star'}
        color={notification.data.color}
      />
    );
  }
  return null;
};

const normalizeNotification = ({ id, created_at: createdAt, read, type, ...otherProps }) => ({
  id,
  created_at: formatCreationDate(createdAt, i18n.language),
  read: read || false,
  type,
  ...otherProps
});

export const normalizeDatabaseNotification = (notification) => {
  // `data` viene como JSON serializado; si está vacío o malformado, JSON.parse LANZA y
  // rompía la normalización de TODA la lista de notificaciones. Parseo defensivo.
  let data = {};
  try {
    data = notification.data ? JSON.parse(notification.data) : {};
  } catch (e) {
    data = {};
  }
  const { type, ...cleanData } = data;

  return normalizeNotification({
    ...notification,
    data: cleanData,
    type
  });
};

export const normalizeBasicNotification = (notification) => normalizeNotification(notification);

export const normalizeSessionNotification = (notification) =>
  normalizeNotification({
    ...notification,
    data: {
      session: notification.session
    }
  });

export const normalizeNewsNotification = (notification) =>
  normalizeNotification({
    ...notification,
    data: {
      news_id: notification.news_id,
      title: notification.title
    }
  });
